/**
 * Admin Route Protection Component
 * Protects admin routes and checks user permissions
 */

import { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { homePathForRole } from '@/lib/auth/roles';
import { useApp } from '@/hooks/use-app-context';
import { AccessDenied } from '@/components/shared/AccessDenied';
import { useTokenGuard } from './use-token-guard';
import type { UserRole } from '@/types/admin.types';

interface AdminRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
}

export function AdminRoute({ children, requiredRole = 'admin' }: AdminRouteProps) {
  const { user } = useApp();
  const { isValid } = useTokenGuard();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';

  const normalizeRole = (value: unknown): UserRole => {
    if (value === 'admin' || value === 'collector' || value === 'moderator' || value === 'user' || value === 'accountant' || value === 'super_admin') {
      return value;
    }
    if (value === 'super_admin' || value === 'SUPER_ADMIN') return 'super_admin';
    if (value === 'ADMIN') return 'admin';
    if (value === 'COLLECTOR' || value === 'COLLECTEUR' || value === 'ANNOTATOR' || value === 'ANNOTATEUR') return 'collector';
    if (value === 'MODERATOR') return 'moderator';
    if (value === 'USER') return 'user';
    if (value === 'COMPTABLE' || value === 'ACCOUNTANT') return 'accountant';

    return 'user';
  };

  if (!isValid || !user) {
    return (
      <Navigate
        to={`/${currentLang}/login`}
        state={{ from: window.location.pathname }}
        replace
      />
    );
  }

  const userRole = normalizeRole(user.role);
  
  const roleHierarchy: Record<UserRole, number> = {
    super_admin: 6,
    admin: 5,
    accountant: 4,
    moderator: 3,
    collector: 2,
    user: 1,
  };

  // Only admins and super admins open the admin panel; any other profile goes
  // back to its own space. Inside it, super-admin pages stay closed to admins.
  if (userRole !== 'admin' && userRole !== 'super_admin') {
    return <Navigate to={homePathForRole(user.role, currentLang)} replace />;
  }

  const hasAccess = roleHierarchy[userRole] >= roleHierarchy[requiredRole];

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}
